import { Combobox, ComboboxButton, ComboboxInput, ComboboxOption, ComboboxOptions, Field } from '@headlessui/react';
import { ChevronUpDownIcon } from '@heroicons/react/16/solid';
import { clsx } from 'clsx';
import { Fragment, ReactNode, useState } from 'react';
import { useController } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';
import { ErrorMessage } from 'softcat:components/error-message.tsx';
import { Form } from 'softcat:components/form.tsx';
import { Spinner } from 'softcat:components/spinner.tsx';
import useSWR, { type BareFetcher } from 'swr';
import { useDebounceCallback } from 'usehooks-ts';

type FormRemoteSearchComboboxProperties<T> = {
    cache: string;
    label: ReactNode;
    name: string;
    options: (data: T) => { label: string; value: string }[];
    placeholder: string;
    service: BareFetcher<T>;
};

export const FormRemoteSearchCombobox = <T,>({ cache, options, label, name, placeholder, service }: FormRemoteSearchComboboxProperties<T>) => {
    const { field, fieldState } = useController({ name });
    const [query, setQuery] = useState('');

    const { data, error, isLoading } = useSWR({ key: cache, limit: 50, query }, service);

    const updateQueryTerm = useDebounceCallback((updatedQueryValue) => {
        setQuery(updatedQueryValue);
    }, 500);

    return (
        <div className="sm:grid sm:grid-cols-4 sm:items-start sm:gap-4">
            <Field as={Fragment}>
                <div className={'flex items-center gap-2'}>
                    <Form.Label htmlFor={name}>{label}</Form.Label>

                    {isLoading && (
                        <div className={'pt-2'}>
                            <Spinner size={'size-3'} />
                        </div>
                    )}
                </div>

                <div className={'mt-2 sm:col-span-3 sm:mt-0'}>
                    <Combobox immediate {...field} as={'div'}>
                        <div className={'relative'}>
                            <ComboboxInput
                                className={clsx(
                                    'relative max-h-36 w-full cursor-default rounded-md border-0 bg-white py-1.5 pl-3 pr-8 text-left text-xs shadow-sm ring-1 focus:outline-none focus:ring-2 sm:leading-6',
                                    {
                                        'text-gray-900 ring-gray-200 focus:ring-indigo-600': !fieldState.error,
                                        'text-red-600 ring-red-600 focus:ring-red-600': fieldState.error,
                                    },
                                )}
                                displayValue={() => {
                                    return (
                                        (data &&
                                            field.value &&
                                            options(data).find((option) => {
                                                return option.value === field.value;
                                            })?.label) ||
                                        field.value
                                    );
                                }}
                                onChange={(event) => {
                                    updateQueryTerm(event.currentTarget.value);
                                }}
                                placeholder={placeholder}
                            />

                            <ComboboxButton className="group absolute inset-y-0 right-0 px-2.5">
                                <ChevronUpDownIcon
                                    aria-hidden="true"
                                    className={clsx('h-5 w-5', {
                                        'text-gray-400': !fieldState.error,
                                        'text-red-600': fieldState.error,
                                    })}
                                />
                            </ComboboxButton>
                        </div>

                        {error && (
                            <div className={'py-2 text-xs'}>
                                <FormattedMessage id={'COMPONENTS_FORM_REMOTE_SEARCH_COMBOBOX_ERROR_LOADING_DATA'} />
                            </div>
                        )}

                        {data && (
                            <>
                                <div>
                                    <ComboboxOptions
                                        anchor="bottom"
                                        className="absolute z-10 mt-2 !max-h-52 w-[var(--input-width)] overflow-auto rounded-md border-0 bg-white py-1 text-sm ring-1 ring-gray-200 empty:invisible focus:outline-none data-[closed]:data-[leave]:opacity-0 data-[leave]:transition data-[leave]:duration-100 data-[leave]:ease-in"
                                        transition
                                    >
                                        {options(data).length > 0 &&
                                            options(data).map((option) => {
                                                return (
                                                    <ComboboxOption
                                                        className="group flex cursor-pointer select-none items-center gap-2 truncate px-4 py-2 text-xs leading-6 text-gray-900 hover:bg-gray-100 data-[focus]:bg-gray-100 data-[selected]:bg-indigo-600 data-[selected]:text-white"
                                                        key={option.value}
                                                        value={option.value}
                                                    >
                                                        {option.label}
                                                    </ComboboxOption>
                                                );
                                            })}

                                        {query !== '' && options(data).length === 0 && (
                                            <ComboboxOption
                                                className="group flex cursor-pointer select-none items-center gap-2 truncate px-4 py-2 text-xs leading-6 text-gray-900 hover:bg-indigo-600 hover:text-white"
                                                key={'no-results'}
                                                value={''}
                                            >
                                                <FormattedMessage id={'COMPONENTS_FORM_REMOTE_SEARCH_COMBOBOX_NO_RESULTS'} />
                                            </ComboboxOption>
                                        )}
                                    </ComboboxOptions>
                                </div>
                            </>
                        )}

                        {fieldState.error && (
                            <div className="mt-3">
                                <ErrorMessage>{fieldState.error.message}</ErrorMessage>
                            </div>
                        )}
                    </Combobox>
                </div>
            </Field>
        </div>
    );
};
